import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Zap } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LANGUAGES } from '../../context/LanguageContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';

export function Pricing({ onBriefClick }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const pkg = t('pricing.package');

  const handleSubscriptionPackageClick = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=auction`);
  };

  const handleFullPricePackageClick = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=one_time`);
  };

  const handleFullPriceWithSupportClick = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=one_time_support`);
  };

  return (
    <section id="pricing" className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-primary font-mono text-sm tracking-wider uppercase mb-4 block">
            Investment
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4 text-slate-900">
            {t('pricing.title')}
          </h2>
          <p className="text-xl text-slate-600">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative"
          data-testid="pricing-card"
        >
          {/* Main pricing card */}
          <div className="bg-white border-2 border-slate-200 rounded-3xl shadow-2xl shadow-slate-200/50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-8 md:px-12 py-8 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold">{pkg.title}</h3>
                  <p className="text-slate-300 mt-1">{pkg.description}</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 font-medium text-sm">{t('pricing.limitedSpots')}</span>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
              <div className="p-8 text-center">
                <p className="text-sm uppercase tracking-wider text-slate-500 mb-3 font-mono">{pkg.oneTime}</p>
                <p className="font-heading text-5xl md:text-6xl font-black text-slate-900">€500</p>
                <p className="text-slate-500 mt-2">one-time payment</p>
                <span className="inline-block mt-3 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                  {t('pricing.supportRequired')}
                </span>
              </div>
              <div className="p-8 text-center bg-slate-50/50">
                <p className="text-sm uppercase tracking-wider text-slate-500 mb-3 font-mono">{pkg.monthly}</p>
                <p className="font-heading text-5xl md:text-6xl font-black text-slate-900">
                  €50<span className="text-2xl text-slate-400">/mo</span>
                </p>
                <p className="text-slate-500 mt-2">{t('pricing.after1stMonth')}</p>
              </div>
            </div>

            {/* Note about requirement */}
            <div className="px-8 md:px-12 py-4 bg-blue-50 border-t border-slate-200">
              <p className="text-sm text-slate-600 text-center">
                {pkg.note}
              </p>
            </div>

            {/* Features */}
            <div className="px-8 md:px-12 py-10 border-t border-slate-200">
              <div className="grid md:grid-cols-2 gap-4">
                {pkg.features.map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="px-8 md:px-12 py-8 bg-slate-50 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-slate-600">
                  <Zap className="text-yellow-500" size={20} />
                  <span>{t('pricing.startBuildingToday')}</span>
                </div>
                <Button 
                  size="lg" 
                  onClick={handleSubscriptionPackageClick}
                  className="magnetic-btn group text-lg px-10"
                  data-testid="pricing-cta-btn"
                >
                  {pkg.cta}
                  <ArrowRight className="inline-block ml-2 group-hover:translate-x-2 transition-transform" size={20} />
                </Button>
              </div>
            </div>
          </div>

          {/* Trust elements below card */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-slate-500">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{t('pricing.securePayment')}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{t('pricing.moneyBackGuarantee')}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              <span className="text-sm">{t('pricing.trustedByClients')}</span>
            </div>
          </div>

          {/* Alternative option - €1500 packages */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900 blur-xl opacity-40" />
            <div className="relative max-w-2xl mx-auto bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-2 border-slate-600/50 rounded-2xl p-8 md:p-10 shadow-2xl">
              {/* Label */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="px-4 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
                  <span className="text-sm text-amber-300 font-semibold">{t('pricing.alternativeLabel')}</span>
                </div>
              </div>

              {/* Title & Price */}
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                  {t('pricing.alternativeTitle')}
                </h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl md:text-6xl font-black text-white">€1,500</span>
                  <span className="text-xl text-slate-300 font-medium">{t('pricing.alternativeOneTime')}</span>
                </div>
              </div>

              {/* What's included */}
              <div className="border-t border-slate-600 pt-6">
                <p className="text-sm text-slate-300 text-center mb-4 font-medium">{t('pricing.alternativeIncludes')}:</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {t('pricing.alternativeFeatures').map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-slate-100 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Two options: with/without subscription */}
              <div className="mt-6 pt-6 border-t border-slate-600">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Without subscription */}
                  <div className="flex flex-col bg-slate-700/40 rounded-lg p-4 border border-slate-600/50">
                    <div className="text-center mb-3">
                      <p className="text-sm text-slate-200 font-semibold mb-2">{t('pricing.withoutSubscription')}</p>
                      <p className="text-sm text-slate-300">{t('pricing.withoutSubscriptionNote')}</p>
                    </div>
                    <Button 
                      size="lg" 
                      onClick={handleFullPricePackageClick}
                      variant="outline"
                      className="group text-white border-slate-500 hover:border-slate-400 hover:bg-slate-700/50 font-semibold"
                    >
                      {t('pricing.orderNoSupport')}
                      <ArrowRight className="inline-block ml-2 group-hover:translate-x-2 transition-transform" size={18} />
                    </Button>
                  </div>

                  {/* With premium subscription */}
                  <div className="flex flex-col bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-lg p-4 border-2 border-green-500/40">
                    <div className="text-center mb-3">
                      <div className="inline-block px-3 py-1 bg-green-500/30 border border-green-400/40 rounded-full mb-2">
                        <span className="text-sm text-green-300 font-bold">{t('pricing.recommended')}</span>
                      </div>
                      <p className="text-sm text-slate-100 font-semibold mb-1">{t('pricing.withPremiumSubscription')}</p>
                      <p className="text-2xl font-black text-white">+ €150<span className="text-base text-slate-200">/mo</span></p>
                      <p className="text-xs text-slate-300 mt-1">{t('pricing.premiumSubscriptionNote')}</p>
                    </div>
                    <Button 
                      size="lg" 
                      onClick={handleFullPriceWithSupportClick}
                      className="group bg-green-600 hover:bg-green-500 text-white font-bold shadow-lg"
                    >
                      {t('pricing.orderWithSupport')}
                      <ArrowRight className="inline-block ml-2 group-hover:translate-x-2 transition-transform" size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
